// "Flow-futások" on the contact's page (7.2 M3): every run of a flow in shadow
// this contact had, newest first, and opened, why — each letter decision with
// its reason and its checks, each step it went through, each move of the run
// (rule 6: afterwards it can be told why a letter would or would not have
// gone). Shadow sends nothing; "küldené" is what the engine would have done.

import { useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchContactFlowRuns } from "@/lib/api";
import type { ContactFlowRun, FlowDecisionRow } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/emc/DataTable";
import { StatusPill } from "@/components/emc/StatusPill";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { TONE_TEXT } from "@/components/emc/tone";
import { cn } from "@/lib/utils";
import {
  checkLook,
  decisionReasonText,
  eventSummary,
  outcomeText,
  outcomeTone,
  runOutcomeText,
  runStateLook,
  runStateText,
  stepResultText,
} from "@/pages/flows/shadowModel";

function letters(run: ContactFlowRun): string {
  if (run.decisions.length === 0) return "—";
  const counts = new Map<string, number>();
  for (const decision of run.decisions) counts.set(decision.outcome, (counts.get(decision.outcome) ?? 0) + 1);
  return [...counts].map(([outcome, value]) => `${outcomeText(outcome)} ${value}`).join(" · ");
}

/** Where the run is or why it ended. */
function whereNow(run: ContactFlowRun): string {
  if (run.state === "waiting" || run.state === "pending" || run.state === "running") {
    if (run.step_id === null) return "—";
    return `#${run.step_id}${run.wake_at ? ` · ${minute(run.wake_at)}` : ""}`;
  }
  return runOutcomeText(run.outcome_reason);
}

const COLUMNS: ColumnDef<ContactFlowRun>[] = [
  {
    id: "flow",
    header: "Flow",
    cell: ({ row }) => (
      <span>
        <Link to={`/flowk/${row.original.flow_id}`} onClick={(event) => event.stopPropagation()}>
          {row.original.flow_name ?? `#${row.original.flow_id}`}
        </Link>{" "}
        <span className="data-mono text-muted-foreground">{row.original.country}</span>
      </span>
    ),
  },
  {
    id: "trigger",
    header: "Indító",
    cell: ({ row }) => minute(row.original.trigger_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "state",
    header: "Állapot",
    cell: ({ row }) => {
      const look = runStateLook(row.original.state);
      return (
        <StatusPill tone={look.tone} title={row.original.state}>
          {look.label}
        </StatusPill>
      );
    },
  },
  {
    id: "where",
    header: "Hol tart · miért ért véget",
    cell: ({ row }) => whereNow(row.original),
    meta: { wrap: true },
  },
  {
    id: "letters",
    header: "Levelek",
    cell: ({ row }) => letters(row.original),
  },
];

function Decision({ decision }: { decision: FlowDecisionRow }) {
  const detail = decision.detail ?? {};
  const checks = detail.checks ?? [];
  const name = detail.message_name;
  return (
    <li className="flex flex-col gap-1 border-t border-line pt-2 first:border-t-0 first:pt-0">
      <p className="m-0 flex flex-wrap items-center gap-2">
        <span className="data-mono text-muted-foreground">#{decision.step_id}</span>
        <StatusPill tone={outcomeTone(decision.outcome)} title={decision.outcome}>
          {outcomeText(decision.outcome)}
        </StatusPill>
        <span title={decision.reason}>{decisionReasonText(decision.reason)}</span>
        <span className="data-mono text-muted-foreground">{minute(decision.scheduled_for)}</span>
        {name ? <span className="text-muted-foreground">· {name}</span> : null}
        {detail.variant ? (
          <span className="text-muted-foreground">
            · {detail.variant === "main" ? "fő levél" : `változat #${detail.variant}`}
          </span>
        ) : null}
        {decision.replay && <span className="text-muted-foreground">· visszatöltött múlt</span>}
        {detail.partial && <span className="text-orange-ink">· hiányos adatból</span>}
      </p>
      {checks.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-0.5 p-0 text-detail">
          {checks.map((check, index) => {
            const look = checkLook(check);
            return (
              <li
                key={`${check.check}-${index}`}
                className={cn(
                  look.ok === false ? TONE_TEXT.danger : look.ok === true ? TONE_TEXT.good : "text-muted-foreground",
                )}
              >
                {look.ok === false ? "✗ " : look.ok === true ? "✓ " : ""}
                {look.text}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function RunDetail({ run }: { run: ContactFlowRun }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-muted-foreground">
        Futás <span className="data-mono">#{run.id}</span> · indító: {run.trigger_kind} · verzió{" "}
        <span className="data-mono">{run.version_id ?? "—"}</span>
        {run.entered ? "" : " · nem lépett be"}
        {run.partial ? " · hiányos adatból döntött" : ""}
        {run.ended_at ? ` · véget ért ${minute(run.ended_at)}` : ""}
      </p>
      <div>
        <p className="caps-label m-0 mb-1">Levél-döntések</p>
        {run.decisions.length === 0 ? (
          <p className="m-0 text-muted-foreground">Ebben a futásban még nem volt levél-lépés.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {run.decisions.map((decision) => (
              <Decision key={decision.id} decision={decision} />
            ))}
          </ul>
        )}
      </div>
      {run.steps.length > 0 && (
        <div>
          <p className="caps-label m-0 mb-1">Lépések</p>
          <p className="m-0 text-muted-foreground">
            {run.steps.map((step, index) => (
              <span key={`${step.step_id}-${index}`}>
                {index > 0 && " → "}
                <span className="data-mono">#{step.step_id}</span> {stepResultText(step.result)}
                {step.replay ? " (múlt)" : ""}
              </span>
            ))}
          </p>
        </div>
      )}
      <div>
        <p className="caps-label m-0 mb-1">Mozgások, időrendben</p>
        {run.events.length === 0 ? (
          <p className="m-0 text-muted-foreground">Nincs naplózott mozgás.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {run.events.map((event, index) => (
              <li key={index} className="text-muted-foreground">
                <span className="data-mono">{minute(event.at)}</span> ·{" "}
                <strong className="font-medium text-ink">
                  {event.from === null ? "" : `${runStateText(event.from)} → `}
                  {runStateText(event.to)}
                </strong>{" "}
                · {eventSummary(event)} · {event.actor}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ContactFlowRuns({ contactId }: { contactId: number }) {
  const [state, reload] = useLoad(() => fetchContactFlowRuns(contactId), [contactId]);
  const [open, setOpen] = useState<number | null>(null);
  const runs = state.data?.runs ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Flow-futások</CardTitle>
        <CardDescription>
          Az árnyék-módú flow-motor futásai ezzel a kontakttal, a legfrissebb elöl. Árnyékban a motor dönt, de
          levelet nem küld: a „küldené” azt jelenti, hogy a flow ennél a lépésnél levelet küldene. Egy sorra
          kattintva látszik, miért.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {state.error !== null && <ErrorState message={state.error} onRetry={reload} />}
        {state.data !== null && runs.length === 0 ? (
          <EmptyState title="Ehhez a kontakthoz még nincs flow-futás." />
        ) : (
          <DataTable
            label="Flow-futások"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={COLUMNS}
            data={runs}
            getRowId={(row) => String(row.id)}
            loading={state.data === null && state.error === null}
            onRowClick={(row) => setOpen(open === row.id ? null : row.id)}
            isRowSelected={(row) => row.id === open}
            renderExpanded={(row) => (open === row.id ? <RunDetail run={row} /> : null)}
          />
        )}
      </CardContent>
    </Card>
  );
}
